import { QueryClient } from '@tanstack/react-query'

import { AGENT_RECOMMEND_TYPE } from '@/lib/api/models/explore'
import { getActivityList, getAgentRecommendList, getExploreList, getTagList } from '@/lib/api/services'

// Query keys - 服务端和客户端共享
export const EXPLORE_LIST_KEY = 'EXPLORE_LIST_KEY' as const
export const FILTER_EXPLORE_LIST_KEY = 'FILTER_EXPLORE_LIST_KEY' as const
export const CATEGORY_LIST_KEY = 'explore-category-list' as const
export const ACTIVITY_LIST_KEY = 'ACTIVITY_LIST_KEY' as const
export const AGENT_RECOMMEND_LIST_KEY = 'AGENT_RECOMMEND_LIST_KEY' as const
/** 桌面端推荐列表 pageSize / 展示数量 */
export const AGENT_RECOMMEND_PAGE_SIZE = 10
/** 移动端推荐列表 pageSize / 展示数量（3 列 × 3 行） */
export const AGENT_RECOMMEND_MOBILE_PAGE_SIZE = 9

export function getAgentRecommendPageSize(isMobile: boolean) {
  return isMobile ? AGENT_RECOMMEND_MOBILE_PAGE_SIZE : AGENT_RECOMMEND_PAGE_SIZE
}

/** MiniSora 桌面端接口 pageSize。首格另有 1 个静态模板，一行仍是 10 列 */
export const MINISORA_PAGE_SIZE = 9
/** MiniSora 移动端接口 pageSize。首格另有 1 个静态模板 */
export const MINISORA_MOBILE_PAGE_SIZE = 8

export function getMinisoraPageSize(isMobile: boolean) {
  return isMobile ? MINISORA_MOBILE_PAGE_SIZE : MINISORA_PAGE_SIZE
}

/** 仅预取首屏 MovieMaker，避免 MiniSora / MiniDrama 拖慢 TTFB 并和下方封面抢带宽 */
export async function prefetchMovieMakerRecommend(queryClient: QueryClient) {
  try {
    await queryClient.prefetchQuery({
      queryKey: [
        AGENT_RECOMMEND_LIST_KEY,
        AGENT_RECOMMEND_TYPE.MOVIEMAKER,
        AGENT_RECOMMEND_PAGE_SIZE
      ],
      queryFn: () =>
        getAgentRecommendList({
          page: 0,
          type: AGENT_RECOMMEND_TYPE.MOVIEMAKER,
          pageSize: AGENT_RECOMMEND_PAGE_SIZE
        })
    })
  } catch {
    // 推荐列表失败不阻断首页 SSR
  }
}

export function getMovieMakerLcpCoverUrl(queryClient: QueryClient): string | undefined {
  const data = queryClient.getQueryData<{
    list?: Array<{ coverUrl?: string; cover_url?: string; sort?: number }>
  }>([AGENT_RECOMMEND_LIST_KEY, AGENT_RECOMMEND_TYPE.MOVIEMAKER, AGENT_RECOMMEND_PAGE_SIZE])

  const first = [...(data?.list ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))[0]
  const url = first?.coverUrl || first?.cover_url
  return url || undefined
}

/**
 * 预取 Explore 页面所需的数据
 * 在服务端组件中调用，数据会通过 HydrationBoundary 传递给客户端
 *
 * 注意：服务端预取返回原始数据（普通对象），
 * 客户端 hooks 会在 useMemo 中进行 plainToInstance 转换。
 */
export async function prefetchExploreData(queryClient: QueryClient) {
  await Promise.all([
    // 预取 explore 列表数据（返回原始数据，客户端会做转换）
    queryClient.prefetchInfiniteQuery({
      queryKey: [EXPLORE_LIST_KEY, 30],
      queryFn: async ({ pageParam = 0 }) => {
        return await getExploreList({ page: pageParam, pageSize: 30 })
      },
      initialPageParam: 0
    }),
    // 预取分类标签数据
    queryClient.prefetchQuery({
      queryKey: [CATEGORY_LIST_KEY, 50],
      queryFn: async () => {
        const res = await getTagList({ page: 0, pageSize: 50 })
        return res.list.map(item => ({ id: item.id.toString(), name: item.name }))
      }
    }),
    // 预取 Apps Banner 数据
    queryClient.prefetchInfiniteQuery({
      queryKey: [ACTIVITY_LIST_KEY],
      queryFn: async () => getActivityList({ page: 0, pageSize: 10 }),
      initialPageParam: 0
    })
  ])
}
